import { FC, memo, ReactNode, useCallback, useRef, useState } from "react";
import { Button } from "../Button/Button";
import { Icon } from "../Icons/Icons";
import "./Section.css";

/**
 * Interface for a section within the Section component
 */
export interface SectionItem {
  /**
   * Content to display in the section
   */
  content?: ReactNode;

  /**
   * Number of columns for grid layout
   * @default 1
   */
  columns: 1 | 2 | 4;

  /**
   * Footer content to display at the bottom of the section
   */
  footer?: ReactNode;
}

/**
 * Props for the Section component
 */
export interface SectionProps {
  /**
   * Title displayed in the section header
   */
  title: string;

  /**
   * Array of section content objects to render
   */
  sections: SectionItem[];

  /**
   * Whether to use compact styling for the section
   * @default false
   */
  compact?: boolean;

  /**
   * Whether to use compact styling for the header
   * @default false
   */
  isHeaderCompact?: boolean;

  /**
   * Custom CSS class string to apply to the content container
   */
  contentContainerStyles?: string;

  /**
   * Whether to add margin below the section
   * @default true
   */
  hasMarginBelow?: boolean;

  /**
   * Whether to show a copy button in the header
   * @default false
   */
  showCopyButton?: boolean;
}

/**
 * Hook for copy to clipboard functionality
 * Extracts text content from DOM element and copies it to clipboard
 */
const useCopyToClipboard = () => {
  const [isCopied, setIsCopied] = useState(false);
  const targetRef = useRef<HTMLDivElement | null>(null);

  /**
   * Extracts text content from HTML element while preserving line breaks
   * @param element - HTML element to extract text from
   * @returns Extracted text with proper line breaks
   */
  const extractTextWithLineBreaks = (element: HTMLElement): string => {
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    );
    let text = "";

    while (walker.nextNode()) {
      const node = walker.currentNode;

      if (node.nodeType === Node.TEXT_NODE) {
        text += (node as Text).data.trim() + " ";
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = (node as HTMLElement).tagName.toLowerCase();
        if (["div", "p", "section", "br"].includes(tag)) {
          text = text.trimEnd() + "\n";
        }
      }
    }

    return text.replace(/\n{2,}/g, "\n").trim();
  };

  /**
   * Copies the content to clipboard
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  const copy = useCallback(() => {
    const el = targetRef.current;
    if (!el) return;

    const text = extractTextWithLineBreaks(el);
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Copy failed", err);
      });
  }, []);

  return { targetRef, isCopied, copy };
};

/**
 * Section Component
 *
 * A flexible section component that displays content in a card-like container
 * with a header and optional copy functionality. Stylistically consistent with
 * the EditableSection component.
 *
 * Features:
 * - Configurable header with title and optional copy button
 * - Customizable grid layouts for content sections
 * - Copy to clipboard functionality
 * - Design system integration with CSS custom properties
 * - Compact and standard sizing options
 *
 * @example
 * // Basic usage
 * <Section
 *   title="User Information"
 *   sections={[
 *     { content: <UserDetails />, columns: 1 }
 *   ]}
 * />
 *
 * @example
 * // With copy functionality and multi-column layout
 * <Section
 *   title="Data Overview"
 *   sections={[
 *     { content: <DataGrid />, columns: 2 },
 *     { footer: <DataSummary />, columns: 1 }
 *   ]}
 *   showCopyButton
 * />
 *
 * @example
 * // Compact header styling
 * <Section
 *   title="Quick Stats"
 *   sections={[{ content: <StatsCards />, columns: 4 }]}
 *   isHeaderCompact
 *   compact
 * />
 */
export const Section: FC<SectionProps> = memo(
  ({
    title,
    sections,
    compact = false,
    isHeaderCompact = false,
    contentContainerStyles,
    hasMarginBelow = true,
    showCopyButton = false,
  }) => {
    const { targetRef: contentRef, isCopied, copy } = useCopyToClipboard();

    /**
     * Returns the appropriate grid class based on column count and compact mode
     * @param columns - Number of columns for the grid layout
     * @returns CSS class string for grid layout
     */
    const getGridColumnsClass = (columns: 1 | 2 | 4): string => {
      const compactClass = compact ? "indigo-section-grid-compact" : "";

      switch (columns) {
        case 1:
          return `indigo-section-grid-1 ${compactClass}`;
        case 2:
          return `indigo-section-grid-2 ${compactClass}`;
        case 4:
          return `indigo-section-grid-4 ${compactClass}`;
        default:
          return `indigo-section-grid-1 ${compactClass}`;
      }
    };

    /**
     * Renders the copy button if enabled
     * @returns Copy button component or null
     */
    const renderCopyButton = () => {
      if (!showCopyButton) return null;

      return (
        <Button
          variant="ghost"
          size="xs"
          onClick={copy}
          disabled={isCopied}
          icon={<Icon name="copy" />}
          className="indigo-section-copy-button"
        >
          {isCopied ? "Copied" : "Copy"}
        </Button>
      );
    };

    return (
      <div
        className={`indigo-section ${
          hasMarginBelow ? "indigo-section-with-margin" : ""
        }`}
      >
        {/* Section Header */}
        <div
          className={`indigo-section-header ${
            isHeaderCompact
              ? "indigo-section-header-compact"
              : "indigo-section-header-standard"
          }`}
        >
          <h2
            className={`indigo-section-title ${
              isHeaderCompact
                ? "indigo-section-title-compact"
                : "indigo-section-title-standard"
            }`}
          >
            {title}
          </h2>
          {renderCopyButton()}
        </div>

        {/* Section Content */}
        <div ref={contentRef} className="indigo-section-content">
          {sections.map((section, index) => (
            <div key={index} className="indigo-section-item">
              {section.content && (
                <div
                  className={`${getGridColumnsClass(section.columns)} ${
                    contentContainerStyles || ""
                  }`}
                >
                  {section.content}
                </div>
              )}
              {section.footer && (
                <div className="indigo-section-footer">{section.footer}</div>
              )}
              {index < sections.length - 1 && (
                <hr className="indigo-section-divider" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  },
);

Section.displayName = "Section";

export default Section;
